import React, { ReactElement, useCallback, useEffect, useState } from "react";
import {
    getCheckBoxMessage,
    getHeaderDetails,
    getOptInBool,
    getOptInBoxVisibility,
    getSubtitle,
    isOtherAmount,
} from "@cart-ui/helpers/DeTip";
import Subtitle from "./components/Subtitle";
import HowItWorks from "./components/HowItWorks";
import DeTipSubtitleLottie from "./components/DeTipSubtitleLottie";

import {
    BottomSheet,
    BottomSheetChildrenProps,
    isEmpty,
    LOTTIE_ASSETS,
    ScreenReaderOnly,
    useEffectOnce,
    LottieAnimation,
    isDateWithinInterval,
    FESTIVE_START_DATE,
    FESTIVE_END_DATE,
    FESTIVE_LOTTIE_ASSETS,
} from "@swiggy-private/core-ui";
import { ElementVisibilitySensor } from "@swiggy-private/dls-web-core/dist/components/ElementVisibilitySensor";
import OptIn from "./components/OptIn";
import { TipDetails, TipExperimentData } from "@swiggy-private/cart-types";
import TippingWidget from "@cart-ui/widgets/de-tipping-widget";
import { getCloudinaryRawUrl } from "@swiggy-private/js-utils";

import styles from "./styles/CartDeTip.scss";
import { TEAM_LINE } from "@swiggy-private/js-utils";

export interface UpdateTipDetailsParams {
    amount: number;
    optIn: boolean;
}

export interface CartDeTipProps {
    tipExperimentData: TipExperimentData;
    tipDetails: TipDetails;
    updateTipDetails: (args: UpdateTipDetailsParams) => void;
    impressionTippingWidget: VoidFunction;
    clickPredefinedTipAmount: (optIn: boolean, tipAmount: number) => void;
    clickCustomTipAmount: (optIn: boolean, tipAmount: number) => void;
    clickTipOptIn: (optIn: boolean, tipAmount: number) => void;
    onImpressionTippingGrouping: () => void;
    onImpressionTippingGroupingActual: () => void;
}

export const DE_TIP_CONTAINER_ID = "de-tip-container";

const lottieComponentProps = {
    loop: false,
    autoplay: true,
    backwardSpeed: 1,
    forwardSpeed: 1,
    playIfVisible: false,
};

const CartDeTip = ({
    tipExperimentData,
    tipDetails,
    updateTipDetails,
    impressionTippingWidget,
    clickCustomTipAmount,
    clickPredefinedTipAmount,
    clickTipOptIn,
    onImpressionTippingGrouping,
    onImpressionTippingGroupingActual,
}: CartDeTipProps): ReactElement => {
    const [errorInfo, setErrorInfo] = useState({
        show: false,
        title: "",
        subtitle: "",
    });
    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const [isOtherSelected, setIsOtherSelected] = useState(false);
    const [otherAmount, setOtherAmount] = useState("");
    const [showLottieAnimation, setShowLottieAnimation] = useState(false);

    useEffectOnce(() => {
        onImpressionTippingGrouping();
    });

    useEffect(() => {
        const _isOtherAmount = isOtherAmount(tipDetails.amount, tipExperimentData.tipOptions);
        const otherAmount = _isOtherAmount ? tipDetails.amount.toString() : "";
        setOtherAmount(otherAmount);
        setIsOtherSelected(!isEmpty(otherAmount));
    }, [tipDetails, tipExperimentData]);

    const { tipMessages } = tipExperimentData;
    const headerDetails = getHeaderDetails(tipMessages);
    const subTitle = getSubtitle(tipMessages);
    const optInBool = getOptInBool(tipDetails, tipExperimentData);
    const checkBoxMessage = getCheckBoxMessage(tipMessages);
    const optInBoxVisibility = getOptInBoxVisibility(tipDetails);

    // TODO: need to remove this festive handling code once config driven changes are implemented
    const isFestivePeriod = isDateWithinInterval(new Date(), FESTIVE_START_DATE, FESTIVE_END_DATE);

    const onCheck = useCallback(
        (flag: boolean) => {
            const amount = tipDetails.amount ? tipDetails.amount : 0;
            clickTipOptIn(flag, amount);
            updateTipDetails({ amount, optIn: !!flag });
        },
        [tipDetails, updateTipDetails, clickTipOptIn],
    );

    const updateTipAmount = useCallback(
        (amount: number) => {
            // const optIn = getOptInBool(tipDetails, tipExperimentData);
            const doesAmountExist = amount > 0;

            updateTipDetails({ amount, optIn: doesAmountExist });

            setShowLottieAnimation(doesAmountExist);
        },
        [updateTipDetails],
    );

    const onVisibilityChange = useCallback(
        (isVisible) => {
            if (isVisible) {
                onImpressionTippingGroupingActual?.();
            }
        },
        [onImpressionTippingGroupingActual],
    );

    useEffectOnce(() => {
        impressionTippingWidget();
    });

    const isTipAmountPresent = !!tipDetails.amount;

    return (
        <ElementVisibilitySensor senseOnce onVisibilityChange={onVisibilityChange}>
            <div id={DE_TIP_CONTAINER_ID} className={styles.deTipRootContainerV2}>
                {showLottieAnimation && isTipAmountPresent && (
                    <LottieAnimation
                        {...lottieComponentProps}
                        fileName={getCloudinaryRawUrl(
                            isFestivePeriod
                                ? FESTIVE_LOTTIE_ASSETS.FESTIVE_DE_TIP_LOTTIE_FILE_ID
                                : LOTTIE_ASSETS.DE_TIP_LOTTIE_FILE_ID,
                            {},
                            {
                                teamLine: TEAM_LINE.PORTAL,
                            },
                        )}
                        className={styles.lottieContainer}
                        visible={isTipAmountPresent}
                        playLottie={isTipAmountPresent}
                    />
                )}

                <div className={styles.topContainer}>
                    <ScreenReaderOnly text={`${headerDetails.head} ${subTitle}`} />

                    <div className={styles.subtitleContainer}>
                        <Subtitle message={subTitle} />
                        <DeTipSubtitleLottie isTipAmountPresent={isTipAmountPresent} />
                    </div>
                </div>

                <TippingWidget
                    tipOptions={tipExperimentData.tipOptions}
                    selectedAmount={tipDetails.amount}
                    otherAmount={otherAmount}
                    isOtherSelected={isOtherSelected}
                    onUpdateTipAmount={updateTipAmount}
                    minValidTipAmount={tipExperimentData.minValidTipAmount}
                    maxValidTipAmount={tipExperimentData.maxValidTipAmount}
                    inputScrollIntoView={100}
                    tipWarnMessage={tipExperimentData.tipMessages.tipWarnMessage}
                    clickCustomTipAmount={clickCustomTipAmount}
                    clickPredefinedTipAmount={clickPredefinedTipAmount}
                    setErrorInfo={setErrorInfo}
                    error={errorInfo}
                    optInBool={optInBool}
                />
                {/* OptIn can be shown when pre-selected amount or other amount and no error */}
                {optInBoxVisibility && (!isOtherSelected || (!errorInfo.show && isOtherSelected)) && (
                    <OptIn onCheck={onCheck} checked={optInBool} message={checkBoxMessage} />
                )}

                {showHowItWorks && (
                    <BottomSheet hideOnOverlayClick onClose={(): void => setShowHowItWorks(false)}>
                        {(bProps: BottomSheetChildrenProps): React.ReactElement => (
                            <HowItWorks
                                details={tipExperimentData.tipMessages.halfCard}
                                onChooseTipAmount={(): void => setShowHowItWorks(false)}
                                {...bProps}
                            />
                        )}
                    </BottomSheet>
                )}
            </div>
        </ElementVisibilitySensor>
    );
};

export default CartDeTip;
